// src/components/MapMarker.ts - CLEAN VERSION
import { Shop, OpeningHours } from "../types/Shop";
import { getShopStatus, ShopStatus } from "../utils/shop";
import { getOptimizedShopIcon } from "../utils/imagekit";

export interface MarkerConfig {
  size: number;
  icon: string;
  iconUrl?: string; // URL to custom uploaded icon/logo
  backgroundColor: string;
  dialProgress?: number; // 0-1 progress value
  isOpen?: boolean;
  showDial?: boolean;
  shadowIntensity?: "light" | "medium" | "heavy";
  shape?: "round" | "square"; // Shape of the marker
}

export interface MarkerStyle {
  background: string;
  icon: string;
  size: number;
}

// Simple configuration
export const DIAL_CONFIG = {
  chunkSizeMinutes: 15,
};

// Responsive marker sizing utility
export const getResponsiveMarkerSize = (baseSize: number): number => {
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // Minimum touch target size for accessibility (44px recommended by Apple/Google)
  const minTouchSize = 44;

  let scaleFactor = 1;

  if (isMobile) {
    // Increase size on mobile for better touch interaction
    scaleFactor = isTouchDevice ? 1.3 : 1.1;
  } else if (isTablet) {
    // Slightly larger on tablets
    scaleFactor = isTouchDevice ? 1.2 : 1.05;
  }

  const responsiveSize = Math.round(baseSize * scaleFactor);

  // Ensure minimum touch target size on touch devices
  if (isTouchDevice && responsiveSize < minTouchSize) {
    return minTouchSize;
  }

  return responsiveSize;
};

// Get responsive cluster marker size
export const getResponsiveClusterSize = (count: number): number => {
  const baseSize = count > 10 ? 50 : count > 5 ? 45 : 40;
  return getResponsiveMarkerSize(baseSize);
};

// Default marker styles
export const MARKER_STYLES: Record<string, MarkerStyle> = {
  shop: {
    background: "#ffffff",
    icon: "🛍️",
    size: 44,
  },
  userLocation: {
    background: "#3b82f6",
    icon: "🧍",
    size: 35,
  },
  customShop: {
    background: "#f59e0b",
    icon: "📍",
    size: 42,
  },
};

// SIMPLIFIED: Only color (green/yellow/gray) based on open/closed/closing soon

// Backwards compatibility
export const isShopCurrentlyOpen = (openingHours?: OpeningHours[]): boolean => {
  return getShopStatus(openingHours).status === "open" || getShopStatus(openingHours).status === "closingSoon";
};

// SIMPLIFIED dial: full color only (green/yellow/gray)
export const createCustomMarker = (config: MarkerConfig & { shopStatus?: ShopStatus }): HTMLElement => {
  const { size, icon, iconUrl, backgroundColor, showDial = true, shopStatus, shape = "round" } = config;

  const markerElement = document.createElement("div");

  // Color logic
  let dialColor = "#9ca3af"; // gray by default
  if (shopStatus === "open") {
    dialColor = "#22c55e"; // green
  } else if (shopStatus === "closingSoon") {
    dialColor = "#facc15"; // yellow
  } // else keep gray for closed

  // Determine border radius based on shape
  const borderRadius = shape === "square" ? "12%" : "50%";
  
  // Determine content: custom icon image or emoji
  const optimizedIconUrl = iconUrl ? getOptimizedShopIcon.large(iconUrl) : null;
  const iconContent = optimizedIconUrl
    ? `<img src="${optimizedIconUrl}" alt="Shop icon" style="width: ${size * 0.7}px; height: ${
        size * 0.7
      }px; object-fit: contain; border-radius: 4px;" loading="lazy" onerror="this.style.display='none'; if(this.nextElementSibling) this.nextElementSibling.style.display='block';" /><span style="display: none; font-size: ${
        size * 0.45
      }px;">${icon}</span>`
    : icon;

  if (!showDial) {
    markerElement.innerHTML = `
      <div style="
        width: ${size}px; 
        height: ${size}px; 
        background-color: ${backgroundColor};
        border-radius: ${borderRadius};
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: ${size * 0.45}px;
        cursor: pointer;
        transition: transform 0.2s ease;
      ">
        ${iconContent}
      </div>
    `;
  } else {
    markerElement.innerHTML = `
      <div style="
        position: relative;
        width: ${size}px; 
        height: ${size}px; 
        cursor: pointer;
        transition: transform 0.2s ease;
        border: ${size * 0.09}px solid ${dialColor};
        border-radius: ${borderRadius};
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: ${backgroundColor};
        color: white;
        font-size: ${size * 0.45}px;
        overflow: hidden;
      ">
        ${iconContent}
      </div>
    `;
  }

  return markerElement;
};

// Factory functions
export const createShopMarker = (shop: Shop): MarkerConfig & { shopStatus: ShopStatus } => {
  const { status } = getShopStatus(shop.openingHours);
  const style = shop.isCustom ? MARKER_STYLES.customShop : MARKER_STYLES.shop;
  return {
    size: getResponsiveMarkerSize(style.size),
    icon: style.icon,
    iconUrl: shop.iconUrl, // Use uploaded business icon if available
    backgroundColor: shop.backgroundColor || style.background, // Use custom color if available
    showDial: !shop.isCustom, // Custom shops don't show opening hours dial
    shadowIntensity: "medium",
    shopStatus: status,
    shape: "round",
  };
};

export const createCustomShopMarker = (shop: Shop): MarkerConfig => {
  const style = MARKER_STYLES.customShop;

  return {
    size: getResponsiveMarkerSize(style.size),
    icon: style.icon,
    iconUrl: shop.iconUrl, // Use uploaded business icon if available
    backgroundColor: shop.backgroundColor || style.background, // Use custom color if available
    showDial: false,
    shadowIntensity: "medium",
    shape: "square",
  };
};

export const createUserLocationMarker = (): MarkerConfig => {
  const style = MARKER_STYLES.userLocation;

  return {
    size: getResponsiveMarkerSize(style.size),
    icon: style.icon,
    backgroundColor: style.background,
    showDial: false,
    shadowIntensity: "medium",
    shape: "round",
  };
};
